<template>
  <footer id="footer" class="footer" aria-labelledby="footer-title">
    <!-- Decorative dashed route line in the background — matches the
         hero's brand language at a much subtler opacity. -->
    <svg class="footer__deco" viewBox="0 0 1440 220" preserveAspectRatio="none" aria-hidden="true">
      <path d="M-20,180 C 320,60 720,220 1080,100 S 1440,180 1480,140"
            fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="2" stroke-dasharray="6 8" />
    </svg>

    <!-- ── CTA strip — editorial "Travel with us" message + app CTA ── -->
    <div class="footer__cta">
      <div class="footer__cta-inner">
        <div class="footer__cta-text">
          <span class="footer__eyebrow">
            <span class="footer__eyebrow-dot" aria-hidden="true" />
            {{ $t('footer.cta_eyebrow', 'Travel-ready in seconds') }}
          </span>
          <h2 class="footer__cta-title">
            {{ $t('footer.cta_title_a', 'Book your next trip') }}
            <span class="footer__cta-accent">{{ $t('footer.cta_title_b', 'on the go.') }}</span>
          </h2>
          <p class="footer__cta-sub">
            {{ $t('footer.cta_sub', 'Download the Abood Bus app — scan tickets, manage holds, and pay with mobile money from anywhere.') }}
          </p>
        </div>
        <div class="footer__cta-actions">
          <a
            class="storebtn storebtn--dark"
            href="https://apps.apple.com/us/app/abood-bus-passenger-app/id1672754974"
            target="_blank" rel="noopener"
          >
            <Icon icon="mdi:apple" />
            <span>
              <small>{{ $t('footer.download_on', 'Download on the') }}</small>
              <strong>App Store</strong>
            </span>
          </a>
          <a
            class="storebtn storebtn--dark"
            href="https://play.google.com/store/apps/details?id=tz.co.eafrica.abood"
            target="_blank" rel="noopener"
          >
            <Icon icon="mdi:google-play" />
            <span>
              <small>{{ $t('footer.get_it_on', 'Get it on') }}</small>
              <strong>Google Play</strong>
            </span>
          </a>
        </div>
      </div>
    </div>

    <!-- ── Top — 5 column grid (brand wide + 4 link cols) ─────────── -->
    <div class="footer__top">
      <div class="footer__top-inner">
        <!-- Brand -->
        <div class="footer__col footer__col--brand">
          <img
            src="@/assets/images/abood_icons.svg"
            :alt="$t('siteUi.aboodLogo')"
            class="footer__logo"
          />
          <p class="footer__tag">
            {{ $t('footer.tagline', 'Premium intercity bus service across Tanzania. Book in a few taps, pay with mobile money, travel in comfort.') }}
          </p>

          <div class="footer__contact-line">
            <a href="tel:+255748771551" class="footer__contact-pill">
              <Icon icon="mdi:phone-in-talk" />
              <span>
                <small>{{ $t('footer.call_us', 'Call us') }}</small>
                <strong>+255 748 771 551</strong>
              </span>
            </a>
            <a href="https://wa.me/255793444333" target="_blank" rel="noopener"
              class="footer__contact-pill footer__contact-pill--wa">
              <Icon icon="mdi:whatsapp" />
              <span>
                <small>WhatsApp</small>
                <strong>+255 793 444 333</strong>
              </span>
            </a>
          </div>

          <div class="footer__socials" :aria-label="$t('siteUi.followAbood')">
            <a href="https://www.facebook.com/people/Abood-Bus-Service/100083366377565/"
              target="_blank" rel="noopener" aria-label="Facebook"
            ><Icon icon="mdi:facebook" /></a>
            <a href="https://www.instagram.com/aboodbustz/"
              target="_blank" rel="noopener" aria-label="Instagram"
            ><Icon icon="mdi:instagram" /></a>
            <a href="https://wa.me/255793444333"
              target="_blank" rel="noopener" aria-label="WhatsApp"
            ><Icon icon="mdi:whatsapp" /></a>
            <a href="https://twitter.com/aboodbus"
              target="_blank" rel="noopener" aria-label="X / Twitter"
            ><Icon icon="mdi:twitter" /></a>
          </div>
        </div>

        <!-- Explore -->
        <div class="footer__col">
          <h4 class="footer__heading">{{ $t('footer.explore', 'Explore') }}</h4>
          <ul class="footer__list">
            <li><router-link to="/">{{ $t('footer.home', 'Home') }}</router-link></li>
            <li><a href="#our-trip">{{ $t('footer.offices', 'Our offices') }}</a></li>
            <li><a href="#routes">{{ $t('footer.routes', 'Routes') }}</a></li>
            <li><a href="#about-us">{{ $t('footer.about', 'About us') }}</a></li>
            <li><a href="#contact">{{ $t('footer.contact', 'Contact') }}</a></li>
          </ul>
        </div>

        <!-- Routes — popular corridors as quick deep-links -->
        <div class="footer__col">
          <h4 class="footer__heading">{{ $t('footer.popular_routes', 'Popular routes') }}</h4>
          <ul class="footer__list">
            <li v-for="r in popularRoutesList" :key="`${r.from}-${r.to}`">
              <a :href="`/schedules-journey?from_name=${encodeURIComponent(r.from)}&to_name=${encodeURIComponent(r.to)}`">
                {{ r.from }} <Icon icon="mdi:arrow-right" /> {{ r.to }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Support / Legal -->
        <div class="footer__col">
          <h4 class="footer__heading">{{ $t('footer.support', 'Support') }}</h4>
          <ul class="footer__list">
            <li>
              <a href="mailto:customercare@aboodbus.co.tz">
                <Icon icon="mdi:email-outline" /> customercare@aboodbus.co.tz
              </a>
            </li>
            <li>
              <a href="#our-trip">
                <Icon icon="mdi:map-marker-outline" /> {{ $t('footer.find_office', 'Find a nearby office') }}
              </a>
            </li>
            <li>
              <!-- Top of the support list on purpose: somebody who has to
                   change or cancel a trip is looking for help NOW, and this is
                   the only self-service route that does not need an account. -->
              <router-link to="/change-ticket">
                <Icon icon="mdi:calendar-edit" /> {{ $t('siteUi.guestAmendTitle') }}
              </router-link>
            </li>
            <li><a href="#">{{ $t('footer.privacy', 'Privacy policy') }}</a></li>
            <li><a href="#">{{ $t('footer.terms', 'Terms of service') }}</a></li>
            <li><a href="#">{{ $t('footer.refunds', 'Refund policy') }}</a></li>
          </ul>
        </div>
      </div>
    </div>

    <!-- ── Trust strip — payment methods we accept ──────────────── -->
    <div class="footer__trust">
      <div class="footer__trust-inner">
        <span class="footer__trust-label">
          {{ $t('footer.we_accept', 'We accept') }}
        </span>
        <span class="footer__trust-icons" :aria-label="$t('siteUi.acceptedPayments')">
          <span class="paypill paypill--mpesa">M-PESA</span>
          <span class="paypill paypill--tigo">Tigo Pesa</span>
          <span class="paypill paypill--airtel">Airtel Money</span>
          <span class="paypill paypill--halo">Halopesa</span>
          <span class="paypill paypill--card"><Icon icon="mdi:credit-card-outline" /> Visa</span>
          <span class="paypill paypill--card"><Icon icon="mdi:credit-card-outline" /> Mastercard</span>
        </span>
      </div>
    </div>

    <!-- ── Bottom — copyright + made-in line + locale ─────────────── -->
    <div class="footer__bottom">
      <div class="footer__bottom-inner">
        <span class="footer__copy" id="footer-title">
          © {{ currentYear }} <strong>Abood Bus Service Limited</strong>.
          {{ $t('footer.all_rights', 'All rights reserved.') }}
        </span>
        <span class="footer__made">
          {{ $t('footer.made_in', 'Made in Tanzania') }}
          <span aria-hidden="true">🇹🇿</span>
          ·
          <span class="footer__since">{{ $t('footer.since', 'Operating since 1986') }}</span>
        </span>
      </div>
    </div>
  </footer>
</template>

<script>
import { Icon } from "@iconify/vue";
import { popularRoutes as getPopularRoutes } from "@/data/aboodRoutes";

export default {
  name: "FooterSection",
  components: { Icon },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      // Top 5 popular corridors as quick-link footer items. Sourced
      // from the single corridor list so adding/removing in
      // `aboodRoutes.js` updates here too.
      popularRoutesList: getPopularRoutes(5),
    };
  },
};
</script>

<style scoped>
/* ── Shell ────────────────────────────────────────────────────── */
.footer {
  position: relative;
  background: linear-gradient(180deg, #050b1c 0%, #0c1a3a 100%);
  color: #cfd6ea;
  padding: 0;
  border-top: 4px solid var(--color-red, #e11d48);
  overflow: hidden;
}
.footer__deco {
  position: absolute;
  inset: auto 0 0 0;
  width: 100%;
  height: 220px;
  pointer-events: none;
  opacity: 0.7;
}

/* ── CTA strip ───────────────────────────────────────────────── */
.footer__cta {
  background: linear-gradient(135deg, rgba(225, 29, 72, 0.18) 0%, rgba(190, 18, 60, 0.06) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 48px 48px;
  position: relative;
  z-index: 1;
}
.footer__cta-inner {
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 32px;
}
.footer__cta-text { max-width: 720px; }
.footer__eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font: 800 11.5px/1 var(--font-sans, sans-serif);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  padding: 6px 12px 6px 8px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  margin-bottom: 16px;
}
.footer__eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--color-red, #e11d48);
  box-shadow: 0 0 0 3px rgba(225, 29, 72, 0.30);
}
.footer__cta-title {
  margin: 0 0 8px;
  font: 800 clamp(26px, 3.4vw, 38px) / 1.15 var(--font-sans, sans-serif);
  letter-spacing: -0.015em;
  color: #fff;
}
.footer__cta-accent {
  background: linear-gradient(90deg, #ff6b9a 0%, var(--color-red, #e11d48) 60%, var(--color-red-600, #be123c) 100%);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
}
.footer__cta-sub {
  margin: 0;
  max-width: 580px;
  font: 500 14.5px/1.55 var(--font-sans, sans-serif);
  color: rgba(207, 214, 234, 0.85);
}
.footer__cta-actions { display: flex; gap: 10px; flex-wrap: wrap; }

/* Store buttons — re-skinned dark variant */
.storebtn--dark {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 11px 18px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  border-radius: 12px;
  text-decoration: none;
  transition: background .15s, border-color .15s, transform .15s;
}
.storebtn--dark:hover {
  background: rgba(225, 29, 72, 0.18);
  border-color: rgba(225, 29, 72, 0.40);
  transform: translateY(-2px);
}
.storebtn--dark svg { font-size: 26px; flex: 0 0 auto; }
.storebtn--dark span { display: flex; flex-direction: column; line-height: 1; }
.storebtn--dark small {
  font: 500 10.5px/1 var(--font-sans, sans-serif);
  opacity: 0.75;
  margin-bottom: 3px;
}
.storebtn--dark strong { font: 800 14px/1 var(--font-sans, sans-serif); }

/* ── Top — 4 columns ─────────────────────────────────────────── */
.footer__top { position: relative; z-index: 1; }
.footer__top-inner {
  width: 100%;
  margin: 0 auto;
  padding: 56px 48px 36px;
  display: grid;
  grid-template-columns: 1.5fr 0.85fr 1fr 1.15fr;
  gap: 40px;
}

.footer__col { min-width: 0; }
.footer__col--brand .footer__logo {
  display: block;
  height: 38px;
  width: auto;
  margin-bottom: 16px;
  filter: brightness(0) invert(1);
  opacity: 0.95;
}
.footer__tag {
  margin: 0 0 18px;
  max-width: 380px;
  font: 500 13.5px/1.6 var(--font-sans, sans-serif);
  color: rgba(207, 214, 234, 0.78);
}

/* Direct contact pills under the tagline */
.footer__contact-line { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
.footer__contact-pill {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 9px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 10px;
  color: #fff;
  text-decoration: none;
  transition: background .15s, border-color .15s;
  width: fit-content;
  max-width: 100%;
}
.footer__contact-pill:hover {
  background: rgba(225, 29, 72, 0.14);
  border-color: rgba(225, 29, 72, 0.32);
}
.footer__contact-pill svg { font-size: 18px; color: var(--color-red, #e11d48); flex: 0 0 auto; }
.footer__contact-pill--wa svg { color: #25D366; }
.footer__contact-pill span { display: flex; flex-direction: column; line-height: 1; }
.footer__contact-pill small {
  font: 500 10.5px/1 var(--font-sans, sans-serif);
  opacity: 0.7; margin-bottom: 3px;
}
.footer__contact-pill strong { font: 800 13px/1 var(--font-sans, sans-serif); }

.footer__socials { display: flex; gap: 8px; }
.footer__socials a {
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.10);
  color: #fff;
  font-size: 18px;
  text-decoration: none;
  transition: background .15s, border-color .15s, transform .15s;
}
.footer__socials a:hover {
  background: rgba(225, 29, 72, 0.18);
  border-color: rgba(225, 29, 72, 0.40);
  transform: translateY(-2px);
}

/* Link columns */
.footer__heading {
  margin: 0 0 16px;
  font: 800 12.5px/1 var(--font-sans, sans-serif);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #fff;
  position: relative;
  padding-bottom: 10px;
}
.footer__heading::after {
  content: "";
  position: absolute;
  left: 0; bottom: 0;
  width: 24px; height: 2px;
  background: var(--color-red, #e11d48);
  border-radius: 2px;
}
.footer__list { list-style: none; padding: 0; margin: 0; }
.footer__list li + li { margin-top: 9px; }
.footer__list a {
  display: inline-flex; align-items: center; gap: 8px;
  font: 500 13.5px/1.4 var(--font-sans, sans-serif);
  color: rgba(207, 214, 234, 0.78);
  text-decoration: none;
  transition: color .15s, transform .15s;
}
.footer__list a:hover { color: #fff; transform: translateX(2px); }
.footer__list a svg { color: rgba(225, 29, 72, 0.85); font-size: 16px; }

/* ── Trust strip ─────────────────────────────────────────────── */
.footer__trust {
  position: relative; z-index: 1;
  background: rgba(0, 0, 0, 0.18);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.footer__trust-inner {
  width: 100%;
  margin: 0 auto;
  padding: 18px 48px;
  display: flex; align-items: center; justify-content: center;
  flex-wrap: wrap; gap: 18px;
}
.footer__trust-label {
  font: 700 11px/1 var(--font-sans, sans-serif);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(207, 214, 234, 0.55);
}
.footer__trust-icons { display: flex; flex-wrap: wrap; gap: 8px; }
.paypill {
  display: inline-flex; align-items: center; gap: 4px;
  font: 700 11px/1 var(--font-sans, sans-serif);
  letter-spacing: 0.04em;
  padding: 6px 10px;
  border-radius: 6px;
  color: #fff;
}
.paypill svg { font-size: 14px; }
.paypill--mpesa  { background: #00A551; }
.paypill--tigo   { background: #0033A0; }
.paypill--airtel { background: #E40000; }
.paypill--halo   { background: #F26522; color: #2a1808; }
.paypill--card   { background: rgba(255,255,255,0.10); border: 1px solid rgba(255,255,255,0.18); color: #fff; }

/* ── Bottom bar ──────────────────────────────────────────────── */
.footer__bottom { position: relative; z-index: 1; }
.footer__bottom-inner {
  width: 100%;
  margin: 0 auto;
  padding: 18px 48px;
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 10px;
  font: 500 12.5px/1.4 var(--font-sans, sans-serif);
  color: rgba(207, 214, 234, 0.65);
}
.footer__copy strong { color: #fff; font-weight: 800; }
.footer__made { font-weight: 600; }
.footer__since { opacity: 0.85; }

/* ── Responsive ─────────────────────────────────────────────── */
@media (max-width: 1180px) {
  .footer__top-inner {
    grid-template-columns: 1.5fr 1fr 1fr;
  }
  .footer__col:nth-child(4) { grid-column: 2 / -1; }
}
@media (max-width: 880px) {
  .footer__cta { padding: 36px 24px; }
  .footer__top-inner {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    padding: 40px 24px 28px;
  }
  .footer__col--brand { grid-column: 1 / -1; }
  .footer__trust-inner { padding: 16px 24px; }
  .footer__bottom-inner { padding: 16px 24px; }
}
@media (max-width: 560px) {
  .footer__cta { padding: 32px 20px; }
  .footer__cta-inner { gap: 22px; }
  .footer__cta-actions { width: 100%; }
  .storebtn--dark { flex: 1; justify-content: center; }
  .footer__top-inner {
    grid-template-columns: 1fr;
    padding: 32px 20px 24px;
    gap: 28px;
  }
  /*
   * Clear the placements inherited from the wider breakpoints.
   *
   * `grid-column: 2 / -1` is set on the fourth column at 1180px and never
   * reset. Against a single-track `1fr` grid it asks for a column that does not
   * exist, so the browser creates an IMPLICIT second one, and the single-column
   * mobile layout silently becomes two auto-sized columns.
   *
   * The visible damage: the brand column collapses to about 87px while the logo
   * inside it is 311px, so the logo spills across the nav and the blurb wraps one
   * word per line.
   */
  .footer__col:nth-child(4),
  .footer__col--brand {
    grid-column: auto;
  }
  /* The logo must never outgrow its column, whatever that column turns out to be. */
  .footer__logo {
    max-width: 100%;
    height: auto;
  }
  .footer__bottom-inner {
    flex-direction: column; text-align: center;
    gap: 8px;
  }
}
</style>
